import { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiEye,
} from 'react-icons/fi';
import {
  apiAdminGetBlogPosts,
  apiDeleteBlogPost,
  apiToggleBlogPost,
} from '../api';
import BlogPostForm from './blog/BlogPostForm';
import { storeUrl } from '../config/urls';
import { confirmAdminAction } from '../utils/adminConfirm';

// Top-level admin screen for blog management.
// Lists every post (incl. drafts) and opens the form drawer for create/edit.
const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiAdminGetBlogPosts();
      setPosts(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (id) => {
    try {
      const res = await apiToggleBlogPost(id);
      setPosts((prev) => prev.map((p) => (p.id === id ? res.data : p)));
    } catch { /* API client displays the error toast. */ }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmAdminAction('This post will be permanently removed.', { title: 'Delete post', confirmLabel: 'Delete' });
    if (!confirmed) return;
    try {
      await apiDeleteBlogPost(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch { /* API client displays the error toast. */ }
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditing(null);
    load();
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading posts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-black">{posts.length} blog posts</p>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition"
        >
          <FiPlus size={16} /> New Post
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-black text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left hidden md:table-cell">Author</th>
              <th className="px-4 py-3 text-left hidden lg:table-cell">Tag</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => (
              <tr key={post.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-black">{post.title}</p>
                  <p className="text-xs text-black truncate max-w-xs">/{post.slug}</p>
                </td>
                <td className="px-4 py-3 text-black hidden md:table-cell">{post.author}</td>
                <td className="px-4 py-3 text-black hidden lg:table-cell">{post.tag || '-'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    post.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-black'
                  }`}>
                    {post.isPublished ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-3">
                    <a
                      href={storeUrl(`/blog/${post.slug}`)}
                      target="_blank" rel="noopener noreferrer"
                      className="text-black hover:text-orange-600" title="View on site"
                    >
                      <FiEye size={16} />
                    </a>
                    <button
                      onClick={() => handleToggle(post.id)}
                      className="text-black hover:text-orange-600"
                      title={post.isPublished ? 'Unpublish' : 'Publish'}
                    >
                      {post.isPublished
                        ? <FiToggleRight size={20} className="text-green-600" />
                        : <FiToggleLeft size={20} />}
                    </button>
                    <button
                      onClick={() => { setEditing(post); setShowForm(true); }}
                      className="text-blue-500 hover:text-blue-700" title="Edit"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="text-red-500 hover:text-red-700" title="Delete"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {posts.length === 0 && (
          <div className="text-center py-12 text-black">No blog posts yet</div>
        )}
      </div>

      {showForm && (
        <BlogPostForm
          post={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default BlogManager;
